import { useEffect, useMemo, useState } from 'react';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import { buildCashFlowSummaryRows } from '../config/buildCashFlowSummaryRows';
import { getCashFlowSummaryColumns } from '../config/CashFlowSummaryColumnConfig';
import { CashFlowSummaryParser } from '../config/CashFlowSummaryParser';
import { CashFlowSummaryRowConfig } from '../config/CashFlowSummaryRowConfig';
import type {
  CashFlowSummaryCellData,
  CashFlowSummaryColumnDefinition,
  CashFlowSummaryInitialObject,
  CashFlowSummarySpreadsheet,
  CashFlowSummaryTableData,
  UseCashFlowSummarySpreadsheetConfigProps,
} from '../config/types';

export const useCashFlowSummarySpreadsheetConfig = ({
  owners,
  firm,
  ownerNames,
  firmName,
}: UseCashFlowSummarySpreadsheetConfigProps) => {
  const [spreadsheet, setSpreadsheet] = useState<CashFlowSummarySpreadsheet | null>(null);

  const rowData = useMemo(
    () => buildCashFlowSummaryRows({ owners, firm, ownerNames, firmName }),
    [owners, firm, ownerNames, firmName]
  );

  const rowConfig = useMemo(() => CashFlowSummaryRowConfig(rowData), [rowData]);
  const columns = useMemo(() => getCashFlowSummaryColumns(), []);

  useEffect(() => {
    if (rowData.length === 0) {
      setSpreadsheet(null);
      return;
    }

    setSpreadsheet(
      new SpreadsheetConfig<
        CashFlowSummaryInitialObject,
        CashFlowSummaryColumnDefinition,
        CashFlowSummaryTableData,
        CashFlowSummaryCellData
      >({
        columns,
        name: 'CashFlowSummary',
        parser: CashFlowSummaryParser,
        rowConfig,
        tableData: { columns },
        format: '',
        showToolbar: false,
        allowDeleteRow: false,
        allowAddRow: false,
      })
    );
  }, [rowData, columns, rowConfig]);

  return {
    spreadsheet,
    hasData: rowData.length > 0,
  };
};
